import * as React from "react";
import {
  Root as SelectPrimitiveRoot,
  Trigger as SelectPrimitiveTrigger,
  Value as SelectPrimitiveValue,
  Icon as SelectPrimitiveIcon,
  Content as SelectPrimitiveContent,
  Viewport as SelectPrimitiveViewport,
  Item as SelectPrimitiveItem,
  ItemText as SelectPrimitiveItemText,
  ItemIndicator as SelectPrimitiveItemIndicator,
  ScrollUpButton as SelectPrimitiveScrollUpButton,
  ScrollDownButton as SelectPrimitiveScrollDownButton,
  Portal as SelectPrimitivePortal,
} from "@radix-ui/react-select";
import { ChevronDown, ChevronUp, Check } from "lucide-react";

import { cn } from "./utils";

const Select = SelectPrimitiveRoot;

const SelectGroup = ({ className, ...props }) => (
  <div className={cn("py-1", className)} {...props} />
);

const SelectValue = SelectPrimitiveValue;

const SelectTrigger = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitiveTrigger
    ref={ref}
    className={cn(
      "flex h-10 w-full items-center justify-between rounded-xl border border-border/70 bg-background px-3 text-sm font-medium text-foreground shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60",
      className
    )}
    {...props}
  >
    <SelectPrimitiveValue />
    <SelectPrimitiveIcon>
      <ChevronDown className="h-4 w-4 text-muted-foreground" />
    </SelectPrimitiveIcon>
  </SelectPrimitiveTrigger>
));
SelectTrigger.displayName = SelectPrimitiveTrigger.displayName;

const SelectContent = React.forwardRef(
  ({ className, children, position = "Popper", ...props }, ref) => (
    <SelectPrimitivePortal>
      <SelectPrimitiveContent
        ref={ref}
        position={position}
        className={cn(
          "z-50 min-w-[10rem] overflow-hidden rounded-2xl border border-border/60 bg-popover text-popover-foreground shadow-lg focus:outline-none",
          className
        )}
        {...props}
      >
        <SelectPrimitiveScrollUpButton className="flex cursor-pointer items-center justify-center py-1 text-muted-foreground transition hover:text-foreground">
          <ChevronUp className="h-4 w-4" />
        </SelectPrimitiveScrollUpButton>
        <SelectPrimitiveViewport className="p-1">{children}</SelectPrimitiveViewport>
        <SelectPrimitiveScrollDownButton className="flex cursor-pointer items-center justify-center py-1 text-muted-foreground transition hover:text-foreground">
          <ChevronDown className="h-4 w-4" />
        </SelectPrimitiveScrollDownButton>
      </SelectPrimitiveContent>
    </SelectPrimitivePortal>
  )
);
SelectContent.displayName = SelectPrimitiveContent.displayName;

const SelectLabel = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("px-3 py-2 text-xs font-semibold uppercase text-muted-foreground", className)}
    {...props}
  />
));
SelectLabel.displayName = "SelectLabel";

const SelectItem = React.forwardRef(({ className, children, ...props }, ref) => (
  <SelectPrimitiveItem
    ref={ref}
    className={cn(
      "relative flex w-full cursor-pointer select-none items-center rounded-xl px-3 py-2 text-sm font-medium outline-none transition hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    {...props}
  >
    <SelectPrimitiveItemIndicator className="absolute left-2 flex items-center justify-center">
      <Check className="h-4 w-4 text-primary" />
    </SelectPrimitiveItemIndicator>
    <SelectPrimitiveItemText>{children}</SelectPrimitiveItemText>
  </SelectPrimitiveItem>
));
SelectItem.displayName = SelectPrimitiveItem.displayName;

const SelectSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("my-1 h-px bg-border/60", className)} {...props} />
));
SelectSeparator.displayName = "SelectSeparator";

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
};

